// src/lib/redux/scoutSettings/scoutSettingsSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ScoutSetting, ScoutSettingErrorResponse } from "../context";

interface ScoutSettingsState {
  scoutSettings: ScoutSetting[];
  scoutSettingsErrors: Record<number, ScoutSettingErrorResponse[]>;
  loading: boolean;
  error: string | null;
  currentSetting: ScoutSetting | null;
  updatingSettings: string[];
}

const initialState: ScoutSettingsState = {
  scoutSettings: [],
  scoutSettingsErrors: {},
  loading: false,
  error: null,
  currentSetting: null,
  updatingSettings: [],
};

const scoutSettingsSlice = createSlice({
  name: "scoutSettings",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setScoutSettings(state, action: PayloadAction<ScoutSetting[]>) {
      state.scoutSettings = action.payload;
      state.loading = false;
      state.error = null;
    },
    updateScoutSettingInState(state, action: PayloadAction<ScoutSetting>) {
      const updatedSetting = action.payload;
      const index = state.scoutSettings.findIndex(
        (setting) => setting.id === updatedSetting.id
      );

      if (index !== -1) {
        state.scoutSettings[index] = updatedSetting;
      } else {
        state.scoutSettings.push(updatedSetting);
      }
    },
    addUpdatingSetting(state, action: PayloadAction<string>) {
      state.updatingSettings.push(action.payload);
    },
    removeUpdatingSetting(state, action: PayloadAction<string>) {
      state.updatingSettings = state.updatingSettings.filter(
        (id) => id !== action.payload
      );
    },
    setCurrentScoutSetting(state, action: PayloadAction<ScoutSetting | null>) {
      state.currentSetting = action.payload;
    },
    resetScoutSettingsState: () => initialState,
    fetchScoutSettingsStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchScoutSettingsSuccess(state, action: PayloadAction<ScoutSetting[]>) {
      state.scoutSettings = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchScoutSettingsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  setLoading,
  setError,
  setScoutSettings,
  updateScoutSettingInState,
  addUpdatingSetting,
  removeUpdatingSetting,
  setCurrentScoutSetting,
  resetScoutSettingsState,
  fetchScoutSettingsStart,
  fetchScoutSettingsSuccess,
  fetchScoutSettingsFailure,
} = scoutSettingsSlice.actions;

export default scoutSettingsSlice.reducer;